import { GearItem, GearOwner, PlayerProfile } from "@highmarch/domain";
import { GearCompare, compareOn } from "@highmarch/app";
import { helps } from "./itemBits";

/** The socket picker's model (picker.ts): one socket's items for one owner, grouped and ordered. Pure over the profile. */

export interface PickRow { readonly item: GearItem; readonly cmp: GearCompare }
export interface PickerModel {
  readonly worn: PickRow | null;
  readonly better: readonly PickRow[];
  /** Items the owner is too low for, per level they need (lowest first). */
  readonly needs: readonly { readonly level: number; readonly rows: readonly PickRow[] }[];
  readonly weaker: readonly PickRow[];
  /** Items the owner can put on now, besides what it wears. */
  readonly fit: number;
  readonly needCount: number;
}

/** Rows before the Weaker group folds into "n more weaker ›". */
export const WEAKER_SHOWN = 5;
/** Best first: more gear here, a bigger primary here, from the stash before off another tower, then oldest. */
function byUse(a: PickRow, b: PickRow): number {
  return b.cmp.power - a.cmp.power || b.cmp.primary.after.cmp(a.cmp.primary.after)
    || Number(a.item.equippedTo !== null) - Number(b.item.equippedTo !== null) || a.item.id - b.item.id;
}

/** The picker's rows for `owner`'s socket `slot`, grouped and ordered (pure over the profile). */
export function pickerModel(profile: PlayerProfile, owner: GearOwner, slot: number): PickerModel {
  const rows = profile.gear.filter((g) => g.slot === slot).map((item): PickRow => ({ item, cmp: compareOn(profile, item, owner) }));
  const worn = rows.find((r) => r.cmp.wearing) ?? null;
  const rest = rows.filter((r) => !r.cmp.wearing);
  const wearable = rest.filter((r) => r.cmp.canWear === true).sort(byUse);
  const locked = rest.filter((r) => r.cmp.needLevel !== null);
  const levels = [...new Set(locked.map((r) => r.cmp.needLevel!))].sort((a, b) => a - b);
  return {
    worn,
    better: wearable.filter((r) => helps(r.cmp)),
    needs: levels.map((level) => ({ level, rows: locked.filter((r) => r.cmp.needLevel === level).sort(byUse) })),
    weaker: wearable.filter((r) => !helps(r.cmp)),
    fit: wearable.length,
    needCount: locked.length,
  };
}
